import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { getCompareColorForField, getMasterValueForField } from '@/tools/evaluationCompare'

export const EVALUATION_PDF_FIELDS = [
    { key: 'inspectionMode', labelKey: 'Choose inspection mode' },
    { key: 'inspectionTool', labelKey: 'Choose inspection tool' },
    { key: 'actualDimension', labelKey: 'Enter actual dimension' },
    { key: 'resultGauge', labelKey: 'Select result gauge' },
    { key: 'nominalDimension', labelKey: 'Enter nominal dimension' },
    { key: 'maximumDimension', labelKey: 'Enter maximum dimension' },
    { key: 'minimumDimension', labelKey: 'Enter minimum dimension' },
    { key: 'upperTolerance', labelKey: 'Enter upper tolerance' },
    { key: 'lowerTolerance', labelKey: 'Enter lower tolerance' },
    { key: 'toleranceValue', labelKey: 'Enter tolerance value' },
    { key: 'permissibleMeasurementError', labelKey: 'Enter permissible measurement error' },
    { key: 'inspectionPointAssessment', labelKey: 'Inspection point assessment' },
    { key: 'rework', labelKey: 'Select rework' }
];

function displayValue(value) {
    if (value === undefined || value === null || value === '') return '-';
    return String(value);
}

function statusFromColor(color, statusLabels) {
    if (color === 'green') return statusLabels.correct;
    if (color === 'red') return statusLabels.wrong;
    return statusLabels.open;
}

function pointStatusFromRows(rows, statusLabels) {
    if (rows.some(row => row.status === statusLabels.wrong)) return statusLabels.wrong;
    if (rows.some(row => row.status === statusLabels.open)) return statusLabels.open;
    return statusLabels.correct;
}

export function buildEvaluationPdfRows({
    workpieces,
    resultsByPointId,
    masterDefinitionsByPointId,
    fieldLabels,
    statusLabels
}) {
    const allRows = [];

    (workpieces || []).forEach(workpiece => {
        (workpiece.inspectionPoints || []).forEach(inspectionPoint => {
            const measurement = resultsByPointId[inspectionPoint._id] || {};
            const masterDefinitions = masterDefinitionsByPointId[inspectionPoint._id] || {};
            const fieldRows = EVALUATION_PDF_FIELDS.map(field => {
                const color = getCompareColorForField({
                    fieldName: field.key,
                    measurement,
                    masterDefinitions,
                    selectedInspectionPoint: inspectionPoint,
                    isUebenChecked: false,
                    routeName: 'EvaluationDetail'
                });
                const masterValue = getMasterValueForField(field.key, masterDefinitions, inspectionPoint);

                return {
                    workpiece: displayValue(workpiece.title || workpiece.number || workpiece._id),
                    inspectionPoint: displayValue(inspectionPoint.title || inspectionPoint.number || inspectionPoint._id),
                    field: fieldLabels[field.key] || field.key,
                    traineeValue: displayValue(measurement[field.key]),
                    masterValue: displayValue(masterValue),
                    status: statusFromColor(color, statusLabels)
                };
            });
            const pointStatus = pointStatusFromRows(fieldRows, statusLabels);
            fieldRows.forEach((row, index) => {
                allRows.push({
                    ...row,
                    pointStatus: index === 0 ? pointStatus : ''
                });
            });
        });
    });

    return allRows;
}

function sanitizeFilename(value) {
    return String(value || 'messkurs-auswertung')
        .trim()
        .replace(/[^\w.-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
        .toLowerCase() || 'messkurs-auswertung';
}

function getPointSummary(rows, statusLabels) {
    return rows.reduce((summary, row) => {
        if (row.pointStatus === statusLabels.correct) summary.correct += 1;
        if (row.pointStatus === statusLabels.wrong) summary.wrong += 1;
        if (row.pointStatus === statusLabels.open) summary.open += 1;
        return summary;
    }, { correct: 0, wrong: 0, open: 0 });
}

function getFieldSummary(rows, statusLabels) {
    return rows.reduce((summary, row) => {
        if (row.status === statusLabels.correct) summary.correct += 1;
        if (row.status === statusLabels.wrong) summary.wrong += 1;
        if (row.status === statusLabels.open) summary.open += 1;
        return summary;
    }, { correct: 0, wrong: 0, open: 0 });
}

function getRowFillColor(row, statusLabels) {
    if (row.status === statusLabels.wrong) return [252, 235, 235];
    if (row.status === statusLabels.correct) return [235, 247, 235];
    return [255, 255, 255];
}

function getStatusTextColor(row, statusLabels) {
    if (row.status === statusLabels.wrong) return [166, 0, 0];
    if (row.status === statusLabels.correct) return [0, 107, 31];
    return [0, 0, 0];
}

export function downloadEvaluationPdf({
    title,
    subtitle,
    rows,
    filename,
    tableLabels,
    statusLabels
}) {
    const pdfRows = Array.isArray(rows) ? rows : [];
    const pdfFilename = `${sanitizeFilename(filename)}.pdf`;
    const margin = 32;
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4'
    });
    const columns = [
        { key: 'workpiece', label: tableLabels.workpiece, width: 105 },
        { key: 'inspectionPoint', label: tableLabels.inspectionPoint, width: 120 },
        { key: 'pointStatus', label: tableLabels.pointStatus, width: 70 },
        { key: 'field', label: tableLabels.field, width: 140 },
        { key: 'traineeValue', label: tableLabels.traineeValue, width: 130 },
        { key: 'masterValue', label: tableLabels.masterValue, width: 130 },
        { key: 'status', label: tableLabels.status, width: 83 }
    ];
    const pointSummary = getPointSummary(pdfRows, statusLabels);
    const fieldSummary = getFieldSummary(pdfRows, statusLabels);

    doc.setProperties({
        title: displayValue(title),
        subject: displayValue(subtitle)
    });

    autoTable(doc, {
        head: [columns.map(column => column.label)],
        body: pdfRows.map(row => columns.map(column => displayValue(row[column.key]))),
        startY: 82,
        margin: { top: 82, right: margin, bottom: 34, left: margin },
        tableWidth: 'wrap',
        theme: 'grid',
        styles: {
            font: 'helvetica',
            fontSize: 7.5,
            cellPadding: 4,
            lineWidth: 0.4,
            lineColor: [204, 209, 214],
            overflow: 'linebreak',
            valign: 'top'
        },
        headStyles: {
            fillColor: [230, 235, 240],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            fontSize: 7.8,
            lineColor: [166, 173, 184]
        },
        columnStyles: columns.reduce((styles, column, index) => {
            styles[index] = { cellWidth: column.width };
            return styles;
        }, {}),
        willDrawPage: () => {
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(15);
            doc.setTextColor(0, 0, 0);
            doc.text(displayValue(title), margin, 34);

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(9);
            doc.setTextColor(64, 64, 64);
            doc.text(displayValue(subtitle), margin, 52, { maxWidth: 760 });

            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 0);
            doc.text(
                `${tableLabels.inspectionPoints}: ${statusLabels.correct}: ${pointSummary.correct}   ${statusLabels.wrong}: ${pointSummary.wrong}   ${statusLabels.open}: ${pointSummary.open}`,
                margin,
                70
            );
            doc.text(
                `${tableLabels.fields}: ${statusLabels.correct}: ${fieldSummary.correct}   ${statusLabels.wrong}: ${fieldSummary.wrong}   ${statusLabels.open}: ${fieldSummary.open}`,
                margin + 245,
                70
            );
        },
        didParseCell: data => {
            if (data.section !== 'body') return;

            const row = pdfRows[data.row.index];
            const column = columns[data.column.index];
            data.cell.styles.fillColor = getRowFillColor(row, statusLabels);

            if (column.key === 'pointStatus' || column.key === 'status') {
                data.cell.styles.fontStyle = 'bold';
            }

            if (column.key === 'status') {
                data.cell.styles.textColor = getStatusTextColor(row, statusLabels);
            }
        },
        didDrawPage: () => {
            const pageNumber = doc.internal.getNumberOfPages();
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(102, 102, 102);
            doc.text(`${tableLabels.page} ${pageNumber}`, doc.internal.pageSize.getWidth() - margin - 48, 577);
        }
    });

    const pdfBytes = new Uint8Array(doc.output('arraybuffer'));
    const magic = String.fromCharCode(...pdfBytes.slice(0, 5));
    if (magic !== '%PDF-') {
        throw new Error(`Invalid jsPDF output: ${magic}`);
    }

    doc.save(pdfFilename);
}
